import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Loader,
  Grid,
  Divider,
  Badge,
  Stack,
  List,
  Group,
  Chip,
  Anchor,
  Paper
} from '@mantine/core';
import {
  IconAlertCircle,
  IconLink,
  IconUsers,
  IconMapPin
} from '@tabler/icons-react';
import { generateClient } from 'aws-amplify/api';
import { Amplify } from 'aws-amplify';
import { getDocument } from '../../../../graphql/queries';

const safeParseContent = (content) => {
  try {
    // Try parsing once
    let parsedContent = JSON.parse(content);

    if (typeof parsedContent === 'string') {
      parsedContent = JSON.parse(parsedContent);
    }

    return parsedContent;
  } catch (error) {
    console.error('Error parsing content:', error);
    return content;
  }
};

export const ViewDocument = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [error, setError] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const isBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 10;
      setIsAtBottom(isBottom);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScroll = () => {
    if (isAtBottom) {
      window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to Top
    } else {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }); // Scroll to Bottom
    }
  };
  const client = generateClient();

  Amplify.configure({
    API: {
      GraphQL: {
        endpoint: process.env.REACT_APP_API_URI,
        region: process.env.REACT_APP_AWS_REGION,
        defaultAuthMode: 'apiKey',
        apiKey: process.env.REACT_APP_APPSYNC_API_KEY
      }
    }
  });
  const GetDocumentById = async () => {
    const data = await client.graphql({
      query: getDocument,
      variables: {
        unique_id: id // Simplified variable structure
      }
    });
    setLoading(false);
    setError(false);
    setData(data);
  };

  useEffect(() => {
    GetDocumentById();
  }, []);

  const content = React.useMemo(() => {
    try {
      return JSON.parse(data?.data?.getDocument?.content || '{}');
    } catch (error) {
      console.error('Error parsing document content:', error);
      return { properties: {} };
    }
  }, [data]);
  console.log(content);
  if (loading) return <Loader size="lg" />;
  if (error) return <Text color="red">Error: {error.message}</Text>;

  const uploadedBy = data?.data?.getDocument?.uploadedBy;

  const uploadedAt = data?.data?.getDocument?.uploadedAt;
  const NewContent = safeParseContent(content);
  console.log(NewContent);
  const date = new Date(uploadedAt);

  const day = date.getUTCDate();
  const month = date.toLocaleString('en-US', {
    month: 'long',
    timeZone: 'UTC'
  });
  const year = date.getUTCFullYear();

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th'; // Covers 4th-20th
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const formattedDate = `${day}${getOrdinalSuffix(day)} ${month} ${year}`;

  console.log(NewContent);

  return (
    <Container size="lg" mt="xl">
      {/* Document Title */}
      {typeof NewContent == 'object' && NewContent ? (
        <Card shadow="lg" padding="xl" radius="lg" withBorder>
          {/* Title */}
          <Title order={2} align="center" mb="md">
            {NewContent.Title || 'Document Details'}
          </Title>
          <Divider my="sm" />

          <Stack spacing="md">
            {/* Incident Type */}
            {NewContent.Incident_Type && (
              <Group>
                <IconAlertCircle size={20} />
                <Text weight={500}>
                  <strong>Incident Type:</strong> {NewContent.Incident_Type}
                </Text>
              </Group>
            )}

            {/* Summary */}
            {NewContent.Summary && (
              <>
                <Title order={4}>Summary</Title>
                <Text
                  style={{ lineHeight: 1.8 }}
                  dangerouslySetInnerHTML={{ __html: NewContent.Summary }}
                />
              </>
            )}

            {/* Findings */}
            {NewContent?.Findings && (
              <>
                <Title order={4}>Findings</Title>
                <Text>{NewContent.Findings}</Text>
              </>
            )}

            {/* Recommendations */}
            {NewContent.Recommendations && (
              <>
                <Title order={4}>Recommendations</Title>
                <Text>{NewContent.Recommendations}</Text>
              </>
            )}

            {/* Keywords */}
            {NewContent.Keywords && NewContent.Keywords.length > 0 && (
              <>
                <Title order={4}>Keywords</Title>
                <Group spacing="xs">
                  {NewContent.Keywords.map((keyword, index) => (
                    <Badge key={index} variant="filled" color="blue">
                      {keyword}
                    </Badge>
                  ))}
                </Group>
              </>
            )}

            {/* Links */}
            {NewContent.Links && NewContent.Links.length > 0 && (
              <>
                <Title order={4}>Links</Title>
                <List>
                  {NewContent.Links.map((link, index) => (
                    <List.Item key={index} icon={<IconLink size={16} />}>
                      <Anchor href={link} target="_blank" color="blue">
                        {link}
                      </Anchor>
                    </List.Item>
                  ))}
                </List>
              </>
            )}

            {/* Entities */}
            {(NewContent.Entities?.Organizations?.length > 0 ||
              NewContent.Entities?.Locations?.length > 0) && (
              <>
                <Title order={4}>Entities</Title>
                <Grid>
                  {NewContent.Entities?.Organizations?.length > 0 && (
                    <Grid.Col span={6}>
                      <Title order={5}>Organizations</Title>
                      <List spacing="xs">
                        {NewContent.Entities.Organizations.map((org, index) => (
                          <List.Item key={index} icon={<IconUsers size={16} />}>
                            {org}
                          </List.Item>
                        ))}
                      </List>
                    </Grid.Col>
                  )}
                  {NewContent.Entities?.Locations?.length > 0 && (
                    <Grid.Col span={6}>
                      <Title order={5}>Locations</Title>
                      <List spacing="xs">
                        {NewContent.Entities.Locations.map((loc, index) => (
                          <List.Item
                            key={index}
                            icon={<IconMapPin size={16} />}
                          >
                            {loc}
                          </List.Item>
                        ))}
                      </List>
                    </Grid.Col>
                  )}
                </Grid>
              </>
            )}
          </Stack>
        </Card>
      ) : (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={2} align="center" mb="md">
            Raw Document Content
          </Title>
          <Divider my="sm" />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-start',
              gap: '1rem'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-start',
                gap: '0.5rem',
                alignItems: 'center'
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                width={24}
                height={24}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                />
              </svg>
              <p>{uploadedBy}</p>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-start',
                gap: '0.5rem',
                alignItems: 'center'
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                width={24}
                height={24}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
                />
              </svg>
              <p>{formattedDate}</p>
            </div>
          </div>
          <Paper
            shadow="sm"
            radius="md"
            p="lg"
            style={{ whiteSpace: 'pre-wrap' }}
          >
            <Text>{content}</Text>
          </Paper>
        </Card>
      )}
    </Container>
  );
};
